'use client'

import type {
	SRT_Column,
	SRT_Localization,
	SRT_RowData,
	SRT_TableInstance,
} from '../../types'

import * as React from 'react'
import { SearchIcon, XIcon } from 'lucide-react'

import { Input } from '@/shared/components/ui/input'
import {
	InputBase,
	InputBaseAdornment,
	InputBaseAdornmentButton,
	InputBaseControl,
	InputBaseInput,
} from '@/shared/components/ui/input-base'

import { cn } from '../../lib/utils'
//import SearchField from '../../../common/SearchField'
import { SRT_TableDateFilter } from './SRT_TableDateFilter'
import { SRT_TableFacetedFilter } from './SRT_TableFacetedFilter'
import { SRT_TableSliderFilter } from './SRT_TableSliderFilter'

type SRT_TableToolbarProps<TData extends SRT_RowData> = {
	table: SRT_TableInstance<TData>
}

export function SRT_TableToolbar<TData extends SRT_RowData>({
	table,
}: SRT_TableToolbarProps<TData>) {
	//const isFiltered = table.getState().columnFilters.length > 0
	const _columns = table.getAllColumns()
	const columns = React.useMemo(
		() => _columns.filter((column) => column.getCanFilter()),
		[_columns]
	)

	/* const onReset = React.useCallback(() => {
		table.resetColumnFilters()
	}, [table]) */

	return (
		<div className='flex flex-1 flex-wrap items-center gap-2'>
			{columns.map((column) => (
				<DataTableToolbarFilter
					key={column.id}
					column={column}
					localization={table.options.localization}
					table={table}
				/>
			))}
			{/* {isFiltered && (
				<Button
					aria-label='Reset filters'
					variant='outline'
					className='border-dashed'
					onClick={onReset}
				>
					<X />
					Reset
				</Button>
			)} */}
		</div>
	)
}
type DataTableToolbarFilterProps<TData extends SRT_RowData> = {
	column: SRT_Column<TData>
	localization: SRT_Localization
	table: SRT_TableInstance<TData>
}

function DataTableToolbarFilter<TData extends SRT_RowData>({
	column,
	localization,
	table,
}: DataTableToolbarFilterProps<TData>) {
	{
		const columnMeta = column.columnDef.meta

		const onFilterRender = React.useCallback(() => {
			if (!columnMeta?.variant) return null

			switch (columnMeta.variant) {
				case 'text':
					return (
						<InputBase className='h-8 w-40 lg:w-56'>
							<InputBaseAdornment>
								<SearchIcon />
							</InputBaseAdornment>
							<InputBaseControl>
								<InputBaseInput
									data-testid={column.id ? `filter-${column.id}` : undefined}
									value={(column.getFilterValue() as string) ?? ''}
									onChange={(e) => {
										column.setFilterValue(e.target.value)
									}}
									placeholder={columnMeta.placeholder ?? columnMeta.label}
								/>
							</InputBaseControl>

							{!!column.getFilterValue() && (
								<InputBaseAdornmentButton
									onClick={() => column.setFilterValue('')}
								>
									<XIcon />
								</InputBaseAdornmentButton>
							)}
						</InputBase>
					)

				case 'number':
					return (
						<div className='relative'>
							<Input
								type='number'
								inputMode='numeric'
								placeholder={columnMeta.placeholder ?? columnMeta.label}
								value={(column.getFilterValue() as string) ?? ''}
								onChange={(event) => column.setFilterValue(event.target.value)}
								className={cn('h-8 w-[120px]', columnMeta.unit && 'pr-8')}
							/>
							{columnMeta.unit && (
								<span className='absolute top-0 right-0 bottom-0 flex items-center rounded-r-md bg-accent px-2 text-sm text-muted-foreground'>
									{columnMeta.unit}
								</span>
							)}
						</div>
					)

				case 'range':
					return (
						<SRT_TableSliderFilter
							column={column}
							title={columnMeta.label ?? column.id}
						/>
					)

				case 'date':
				case 'date-range':
					return (
						<SRT_TableDateFilter
							localization={localization}
							column={column}
							title={columnMeta.label ?? column.id}
							multiple={columnMeta.variant === 'date-range'}
						/>
					)

				case 'select':
				case 'multi-select':
					return (
						<SRT_TableFacetedFilter
							table={table}
							column={column}
							title={columnMeta.label ?? column.id}
							options={columnMeta.options ?? []}
							multiple={columnMeta.variant === 'multi-select'}
						/>
					)

				default:
					return null
			}
		}, [column, columnMeta])

		return onFilterRender()
	}
}
